import {css} from '@mui/material';
import {ConfigObjectType, enumContainer} from '@wallaby-ui/core';
import {
  LightHierarchyFilter,
  LightMonthRangeFilter,
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectedFiltersPane,
  LightSelectFilter,
  MedicationAtc,
  NormalizedOption,
  ShortcutEnum,
  useDrpCauseBackground,
  useDRPMedicationData,
  useFormatters,
  UserFilterDto,
  useOrganizations,
} from '@wallaby-ui/document';
import {sortBy} from 'lodash';
import * as React from 'react';
import {useCallback} from 'react';

import {CHART_TYPE_OPTIONS, FiltersData, InterventionsDepartmentReportFilter} from './departmenttypes';
import {useAppSelector} from '../../../store_hooks';
import {useMedicationOptions} from '../../hospitalizations_detail/HospitalizationPhRecommendationV2';

type HospitalReportFiltersProps = {
  filters: InterventionsDepartmentReportFilter;
  onFiltersChange: (filters: Partial<InterventionsDepartmentReportFilter>) => void;
  savedFiltersOpen: boolean;
  onToggleSavedFilters: () => void;
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  config: ConfigObjectType;
  onSetFilter: (newFilter: InterventionsDepartmentReportFilter) => void;
  defaultReportFilters?: FiltersData['report'];
  hasActiveFilter?: boolean;
};
export function normalizeInterventionsDepartmentReportFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}

export const DepartmentReportFilters: React.FC<HospitalReportFiltersProps> = ({
  filters,
  onFiltersChange,
  savedFiltersOpen,
  onToggleSavedFilters,
  storageIdPrefix,
  component,
  userFiltersLoading,
  addedUserFilters,
  userFilters,
  changeUserFilter,
  config,
  onSetFilter,
  defaultReportFilters,
  hasActiveFilter,
}) => {
  const formatters = useFormatters();
  const {t, formatDate, tEnum} = formatters;

  const defaultHospitalDepartmentReportFilters = defaultReportFilters;

  const handleDateChange = (newValue: {startDate?: string; endDate?: string; shortcut?: ShortcutEnum | null} | null) => {
    onFiltersChange({
      dateFrom: newValue?.startDate,
      dateTo: newValue?.endDate,
      floatingShortcutName: newValue?.shortcut,
    });
  };
  const handleMeasureTypeChange = (newValue: any) => {
    onFiltersChange({measureType: newValue});
  };

  const handleOrganizationIdsChange = (newValue: string[]) => {
    onFiltersChange({organizationIds: newValue as any});
  };

  const handleAtcHierarchyIdsChange = (newValue: any) => {
    onFiltersChange({atcHierarchyIds: newValue != null ? newValue.map((value: string | number) => +value) : undefined});
  };

  const handleDrpTypesChange = (newValue: any) => {
    onFiltersChange({types: newValue});
  };

  const handleDrpManifestationsChange = (newValue: any) => {
    onFiltersChange({manifestations: newValue ? [newValue] : undefined});
  };

  const handleDrpCausesChange = (newValue: any) => {
    onFiltersChange({causes: Array.isArray(newValue) && newValue.length > 0 ? newValue : undefined});
  };

  const handleDrpPlannedInterventionsChange = (newValue: any) => {
    onFiltersChange({plannedInterventions: Array.isArray(newValue) && newValue.length > 0 ? newValue : undefined});
  };

  const handleCausesAggregationTypeChange = (newValue: any) => {
    onFiltersChange({causesAggregationType: newValue});
  };

  const handleChartValueChange = (newValue: any) => {
    onFiltersChange({chartValueSelection: newValue});
  };

  const handleChartTypeChange = (newValue: any) => {
    onFiltersChange({chartTypeSelection: newValue});
  };

  const {getDRPMedicationData, medications} = useDRPMedicationData(config);
  const {medicationAtcOptions} = useMedicationOptions({
    medications,
    getDRPMedicationData,
    config,
    selectedMedicationName: null,
  });

  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const {data: organizations} = useOrganizations(config);
  const getDrpCauseBackground = useDrpCauseBackground();
  const getDrpCauseBackgroundColor = useCallback(
    (cause: any) =>
      css({
        backgroundColor: getDrpCauseBackground(cause),
      }),
    [getDrpCauseBackground]
  );

  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('hospital.network.report.filter.date'),
          value: `${formatDate(filters.dateFrom ?? '')} - ${formatDate(filters.dateTo ?? '')}`,
          defaultValue: [
            defaultHospitalDepartmentReportFilters?.dateFrom,
            defaultHospitalDepartmentReportFilters?.dateTo,
            defaultHospitalDepartmentReportFilters?.floatingShortcutName,
          ],
        },
        {
          name: 'organizationIds',
          label: t('documents.list.origin'),
          value: (filters.organizationIds ?? [])
            .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName ?? String(id))
            .join(', '),
        },
        {
          name: 'atcHierarchyIds',
          label: t('hospitalization.detail.phRec.drp.medicationAtcId'),
          value: (filters.atcHierarchyIds ?? [])
            .map((id) => medicationAtcOptions?.find((w) => String(w.id) === String(id))?.label ?? String(id))
            .join(', '),
        },
        {
          name: 'types',
          label: t('hospitalization.detail.phRec.drp.type'),
          value: (filters.types ?? []).map((v) => tEnum('DrpType', v)).join(', '),
        },
        {
          name: 'manifestations',
          label: t('filter.recommendation.drpFilterManifestation'),
          value: (filters.manifestations ?? []).map((v) => tEnum('DrpManifestation', v)).join(', '),
        },
        {
          name: 'causes',
          label: t('hospitalization.detail.phRec.drp.causes'),
          value: (filters.causes ?? []).map((v) => tEnum('DrpCause', v)).join(', '),
        },
        {
          name: 'causesAggregationType',
          label: t('department.report.filter.causesAggregationType'),
          value: tEnum('InterventionsReportDbParamCauseAggregationType', filters.causesAggregationType ?? undefined),
          defaultValue: defaultHospitalDepartmentReportFilters?.causesAggregationType,
        },
        {
          name: 'plannedInterventions',
          label: t('hospitalization.detail.phRec.drp.plannedInterventions'),
          value: (filters.plannedInterventions ?? []).map((v) => tEnum('DrpPlannedIntervention', v)).join(', '),
        },
        {
          name: 'measureType',
          label: t('hospital.network.report.filter.aggregation'),
          value: tEnum('InterventionsReportDbParamMeasureType', filters.measureType ?? undefined),
          defaultValue: defaultHospitalDepartmentReportFilters?.measureType,
        },
        {
          name: 'chartValueSelection',
          label: t('department.report.filter.chartvalues'),
          value: tEnum('InterventionsDepartmentReportChartValueSelection', filters.chartValueSelection ?? undefined),
          defaultValue: defaultHospitalDepartmentReportFilters?.chartValueSelection,
        },
        {
          name: 'chartTypeSelection',
          label: t('department.report.charts.type'),
          value: t(CHART_TYPE_OPTIONS.find((w) => w.id === filters.chartTypeSelection)?.messageKey ?? ''),
          defaultValue: defaultHospitalDepartmentReportFilters?.chartTypeSelection,
        },
      ]}
      tableFilters={filters as Record<string, any>}
      changeTableFilters={(newTableFilters) => onFiltersChange(newTableFilters as Partial<InterventionsDepartmentReportFilter>)}
      formatters={formatters}
    />
  );
  return (
    <LightFilterPanel
      open={savedFiltersOpen}
      toggleOpen={onToggleSavedFilters}
      hasActiveFilter={hasActiveFilter}
      savedFiltersProps={{
        storageIdPrefix,
        component,
        userFiltersLoading,
        addedUserFilters,
        userFilters,
        changeUserFilter,
        filter: filters as Record<string, any>,
        setFilter: (newFilter) => onSetFilter(newFilter as InterventionsDepartmentReportFilter),
        config,
        normalizeForCompare: normalizeInterventionsDepartmentReportFiltersForCompare,
      }}
      selectedFilters={selectedFiltersPane}
    >
      <LightMonthRangeFilter
        config={{
          name: 'reportDate',
          ui: {label: 'hospital.network.report.filter.date'},
        }}
        value={
          filters.dateFrom && filters.dateTo
            ? {
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                shortcut: filters.floatingShortcutName,
              }
            : null
        }
        defaultValue={
          defaultHospitalDepartmentReportFilters?.dateFrom && defaultHospitalDepartmentReportFilters?.dateTo
            ? {
                startDate: defaultHospitalDepartmentReportFilters?.dateFrom,
                endDate: defaultHospitalDepartmentReportFilters?.dateTo,
                shortcut: defaultHospitalDepartmentReportFilters?.floatingShortcutName,
              }
            : null
        }
        onChange={handleDateChange}
        isRemoveFilterButtonHidden
        showShortcuts
      />
      <LightOrganizationFilter2
        appConfig={config}
        config={{
          name: 'organizationIds',
          ui: {
            label: 'documents.list.origin',
          },
        }}
        multi
        value={filters.organizationIds}
        onChange={handleOrganizationIdsChange}
      />
      <LightHierarchyFilter
        config={{name: 'medicationAtcId', ui: {label: 'hospitalization.detail.phRec.drp.medicationAtcId'}}}
        options={medicationAtcOptions}
        multi
        value={filters.atcHierarchyIds != null ? filters.atcHierarchyIds.map((value) => String(value)) : null}
        onChange={handleAtcHierarchyIdsChange}
        ignoreOption={(option: NormalizedOption<MedicationAtc>) => option.original.isSoloRootNode}
      />
      <LightSelectFilter
        config={{name: 'type', ui: {label: 'hospitalization.detail.phRec.drp.type'}}}
        value={filters.types}
        onChange={handleDrpTypesChange}
        multi
        options={sortByMessageKey(enumMap.DrpType ?? [])}
      />
      <LightSelectFilter
        config={{
          name: 'manifestation',
          ui: {label: 'filter.recommendation.drpFilterManifestation'},
        }}
        options={sortByMessageKey(enumMap.DrpManifestation ?? [])}
        value={filters.manifestations}
        onChange={handleDrpManifestationsChange}
      />
      <LightHierarchyFilter
        config={{name: 'causes', ui: {label: 'hospitalization.detail.phRec.drp.causes'}}}
        options={sortByMessageKey(enumMap.DrpCause ?? []).map((v) => ({
          ...v,
          disabled: v.hasChildren,
        }))}
        multi
        defaultIsExpanded
        value={filters.causes}
        onChange={handleDrpCausesChange}
        chipCss={getDrpCauseBackgroundColor}
      />
      <LightHierarchyFilter
        config={{name: 'causesAggregationType', ui: {label: 'department.report.filter.causesAggregationType'}}}
        options={sortByMessageKey(enumMap.InterventionsReportDbParamCauseAggregationType ?? [])}
        value={filters.causesAggregationType}
        onChange={handleCausesAggregationTypeChange}
        isClearable={false}
      />
      <LightSelectFilter
        config={{name: 'plannedInterventions', ui: {label: 'hospitalization.detail.phRec.drp.plannedInterventions'}}}
        options={sortByMessageKey(enumMap.DrpPlannedIntervention ?? [])}
        multi
        value={filters.plannedInterventions}
        onChange={handleDrpPlannedInterventionsChange}
      />
      <LightSelectFilter
        config={{
          name: 'reportAggregation',
          ui: {label: 'hospital.network.report.filter.aggregation'},
        }}
        options={sortByMessageKey(enumMap.InterventionsReportDbParamMeasureType ?? [])}
        maxWidth='362px'
        value={filters.measureType}
        onChange={handleMeasureTypeChange}
        isClearable={false}
      />
      <LightSelectFilter
        config={{
          name: 'chartType',
          ui: {label: 'department.report.filter.chartvalues'},
        }}
        options={sortByMessageKey(enumMap.InterventionsDepartmentReportChartValueSelection ?? [])}
        maxWidth='362px'
        value={filters.chartValueSelection}
        onChange={handleChartValueChange}
        isClearable={false}
      />
      <LightSelectFilter
        config={{
          name: 'chartType',
          ui: {label: 'department.report.charts.type'},
        }}
        options={CHART_TYPE_OPTIONS}
        maxWidth='362px'
        value={filters.chartTypeSelection}
        onChange={handleChartTypeChange}
        isClearable={false}
      />
    </LightFilterPanel>
  );
};
